import { type ReactElement, useMemo } from 'react'
import clsx from 'clsx'
import type { TraceStep } from '../types'

/**
 * Rendered tree view: a step's row plus the indented depth derived
 * from the parent_step_id chain. Computed once per `steps` change.
 */
interface IndentedStep {
  step: TraceStep
  depth: number
}

export interface TraceTimelineProps {
  steps: TraceStep[]
  selectedStepId: string | null
  onSelect: (stepId: string) => void
}

/**
 * Vertical list of trace steps with agent-role badges + indentation
 * for the orchestrator → subagent → tool tree. The forensics page
 * (AGNT-685) uses this as the primary navigation surface — clicking a
 * step opens its inputs/outputs in StepInspector (PR 2f) and the
 * source viewer.
 *
 * Steps arrive pre-sorted by `started_at` from the backend; we don't
 * re-sort here. Parent/child relationships are read from
 * `parent_step_id` to compute depth — independent of order.
 */
export function TraceTimeline({
  steps,
  selectedStepId,
  onSelect,
}: TraceTimelineProps): ReactElement {
  const indented = useMemo(() => buildIndentedSteps(steps), [steps])

  if (indented.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic px-3 py-4">
        No steps recorded for this trace.
      </div>
    )
  }

  return (
    <ol className="flex flex-col" data-testid="trace-timeline">
      {indented.map(({ step, depth }) => (
        <li key={step.step_id}>
          <button
            type="button"
            data-testid={`trace-step-${step.step_id}`}
            data-selected={step.step_id === selectedStepId ? 'true' : 'false'}
            onClick={() => onSelect(step.step_id)}
            className={clsx(
              'w-full text-left px-3 py-2 border-l-2 hover:bg-muted',
              'flex items-center gap-2 text-sm',
              step.status === 'error'
                ? 'border-l-red-400'
                : step.observation_type === 'GENERATION'
                  ? 'border-l-purple-400'
                  : 'border-l-blue-400',
              step.step_id === selectedStepId && 'bg-blue-50 dark:bg-blue-950',
            )}
            style={{ paddingLeft: `${0.75 + depth * 1}rem` }}
          >
            <StepBadge agentRole={step.agent_role} stepKind={step.step_kind} />
            <span className="font-mono text-xs truncate flex-1" title={step.tool_name}>
              {step.tool_name}
            </span>
            {step.duration_ms !== null && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatDuration(step.duration_ms)}
              </span>
            )}
            {step.status === 'error' && (
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">err</span>
            )}
          </button>
        </li>
      ))}
    </ol>
  )
}

/**
 * Compact stage pill. native-agent steps badge off `step_kind` (toolgen /
 * tool_exec / …); kg-agent steps off `agent_role` (one of the seven roles).
 * Falls back to a neutral "unknown" pill when neither is present (older
 * traces that don't carry the metadata).
 */
function StepBadge({
  agentRole,
  stepKind,
}: {
  agentRole: string | null
  stepKind: string | null
}): ReactElement {
  const kind = stepKind ?? agentRole ?? 'unknown'
  // Native kinds get explicit short labels (a naive 4-char slice collides:
  // toolgen / tool_exec both → "TOOL"); kg-agent roles keep the 4-char slice.
  const label = NATIVE_KIND_LABELS[kind] ?? kind.slice(0, 4)
  const tone = STEP_TONES[kind] ?? 'bg-gray-100 text-gray-700'
  return (
    <span
      className={clsx(
        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide shrink-0',
        tone,
      )}
      title={kind}
    >
      {label}
    </span>
  )
}

/** Distinct short labels for native stage kinds (avoids slice collisions). */
const NATIVE_KIND_LABELS: Record<string, string> = {
  invocation: 'LOOP',
  // Without an entry the badge falls back to `kind.slice(0, 4)` — `inte`, in neutral grey.
  intent: 'INT',
  query_understanding: 'QU',
  critic_pre: 'PRE',
  toolgen: 'GEN',
  tool_exec: 'EXEC',
  summarize: 'SUM',
  critic_post: 'CPST',
  post_processor: 'GATE',
  // query-understanding layer (native.qu.*): the split, each leg, the reconciliation
  'qu.plan': 'PLAN',
  'qu.leg': 'LEG',
  'qu.reconcile': 'RECON',
  'qu.leg.resolve': 'RSLV',
  'qu.leg.condense': 'COND',
}

const STEP_TONES: Record<string, string> = {
  // kg-agent roles
  orchestrator: 'bg-amber-100 text-amber-800',
  connectivity: 'bg-cyan-100 text-cyan-800',
  isolation: 'bg-rose-100 text-rose-800',
  inventory: 'bg-emerald-100 text-emerald-800',
  spec: 'bg-indigo-100 text-indigo-800',
  freeform: 'bg-gray-200 text-gray-800',
  summarizer: 'bg-violet-100 text-violet-800',
  // native stages — every kind in NATIVE_KIND_LABELS gets a tone here so none
  // silently falls back to the neutral default.
  invocation: 'bg-gray-200 text-gray-800',
  // Its own hue: the intent row is a routing DECISION, not a loop stage, and it is the row a
  // reader jumps to when asking why a turn went to the lane it did.
  intent: 'bg-fuchsia-100 text-fuchsia-800',
  query_understanding: 'bg-sky-100 text-sky-800',
  // critic_pre / critic_post share the orange "critic" tone (same stage role,
  // different position in the loop).
  critic_pre: 'bg-orange-100 text-orange-800',
  toolgen: 'bg-amber-100 text-amber-800',
  tool_exec: 'bg-cyan-100 text-cyan-800',
  summarize: 'bg-violet-100 text-violet-800',
  critic_post: 'bg-orange-100 text-orange-800',
  post_processor: 'bg-emerald-100 text-emerald-800',
  'qu.plan': 'bg-sky-100 text-sky-800',
  'qu.leg': 'bg-sky-100 text-sky-800',
  'qu.reconcile': 'bg-sky-100 text-sky-800',
  'qu.leg.resolve': 'bg-sky-100 text-sky-800',
  'qu.leg.condense': 'bg-sky-100 text-sky-800',
}

/**
 * Compact duration: `<1s` → `Nms`, `<60s` → `N.NNs`, else `MM:SS`.
 * Right-aligned in the timeline so the SLA scanning mode works.
 */
export function formatDuration(ms: number): string {
  if (ms < 1_000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}s`
  const minutes = Math.floor(ms / 60_000)
  const seconds = Math.floor((ms % 60_000) / 1_000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Walk the parent_step_id chain to assign each step a tree depth.
 * Stable: the input order is preserved (steps come pre-sorted by
 * started_at). Cycles or missing parents fall back to depth 0
 * defensively — never throws.
 */
export function buildIndentedSteps(steps: TraceStep[]): IndentedStep[] {
  const byId = new Map<string, TraceStep>()
  for (const step of steps) byId.set(step.step_id, step)

  const depthOf = (step: TraceStep, seen = new Set<string>()): number => {
    if (!step.parent_step_id) return 0
    if (seen.has(step.step_id)) return 0 // cycle guard
    const parent = byId.get(step.parent_step_id)
    if (!parent) return 0 // orphan parent (truncated trace) → root
    seen.add(step.step_id)
    return 1 + depthOf(parent, seen)
  }

  return steps.map((step) => ({ step, depth: Math.min(depthOf(step), 8) }))
}
